import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Container,
    Paper,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tabs,
    Tab,
    Button,
    CircularProgress,
    Alert,
    Chip,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon, Assessment as AssessmentIcon } from '@mui/icons-material';
import axios from 'axios';
import { workshopDomain } from '../../util/domain';

const Reports = () => {
    const navigate = useNavigate();
    const [events, setEvents] = useState([]);
    const [selectedEvent, setSelectedEvent] = useState('');
    const [activeTab, setActiveTab] = useState(0); // 0 for states, 1 for users
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    // States report data
    const [statesData, setStatesData] = useState([]);
    const [statesTotalCount, setStatesTotalCount] = useState(0);
    const [selectedState, setSelectedState] = useState(null);
    const [collegesData, setCollegesData] = useState([]);
    const [collegesTotalCount, setCollegesTotalCount] = useState(0);
    const [missingCollegeCodeCount, setMissingCollegeCodeCount] = useState(0);
    const [loadingStates, setLoadingStates] = useState(false);
    const [loadingColleges, setLoadingColleges] = useState(false);

    // Users report data
    const [usersData, setUsersData] = useState([]);
    const [usersTotalRegistrations, setUsersTotalRegistrations] = useState(0);
    const [usersUnassignedCount, setUsersUnassignedCount] = useState(0);
    const [usersTotalWithUnassigned, setUsersTotalWithUnassigned] = useState(0);
    const [selectedUser, setSelectedUser] = useState(null);
    const [userCollegesData, setUserCollegesData] = useState([]);
    const [userCollegesTotalCount, setUserCollegesTotalCount] = useState(0);
    const [loadingUsers, setLoadingUsers] = useState(false);
    const [loadingUserColleges, setLoadingUserColleges] = useState(false);

    // Check user role and authentication on mount
    useEffect(() => {
        const token = localStorage.getItem('token');
        if (!token) {
            navigate('/admin');
            return;
        }

        const user = JSON.parse(localStorage.getItem('user') || '{}');
        const roles = user.roles || [];
        const isAdmin = roles.includes('Admin');
        if (!isAdmin) {
            navigate('/admin/home');
            return;
        }

        fetchEvents();
    }, [navigate]);

    const fetchEvents = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${workshopDomain}/api/events/all`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.data.success) {
                // Sort events by created_at descending (most recent first)
                const sortedEvents = [...response.data.events].sort((a, b) => {
                    const dateA = new Date(a.created_at || a.registration_open_date);
                    const dateB = new Date(b.created_at || b.registration_open_date);
                    return dateB - dateA; // Descending order (newest first)
                });
                setEvents(sortedEvents);
            }
        } catch (error) {
            console.error('Error fetching events:', error);
            setError('Failed to fetch events');
        }
    };

    const fetchStatesReport = async () => {
        if (!selectedEvent) return;

        setLoadingStates(true);
        setError('');
        setSelectedState(null);
        setCollegesData([]);
        setCollegesTotalCount(0);
        setMissingCollegeCodeCount(0);

        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${workshopDomain}/api/reports/states/${selectedEvent}`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.success) {
                setStatesData(response.data.data || []);
                setStatesTotalCount(response.data.totalCount || 0);
            } else {
                setError('Failed to fetch states report');
            }
        } catch (error) {
            console.error('Error fetching states report:', error);
            setError(error.response?.data?.message || 'Failed to fetch states report');
        } finally {
            setLoadingStates(false);
        }
    };

    const fetchCollegesForState = async (stateName) => {
        if (!selectedEvent || !stateName) return;

        setLoadingColleges(true);
        setError('');

        try {
            const token = localStorage.getItem('token');
            const encodedStateName = encodeURIComponent(stateName);
            const response = await axios.get(
                `${workshopDomain}/api/reports/states/${selectedEvent}/colleges/${encodedStateName}`,
                {
                    headers: { Authorization: `Bearer ${token}` }
                }
            );

            if (response.data.success) {
                setCollegesData(response.data.data || []);
                setCollegesTotalCount(response.data.totalCount || 0);
                setMissingCollegeCodeCount(parseInt(response.data.missingCollegeCodeCount) || 0);
                setSelectedState(stateName);
            } else {
                setError('Failed to fetch colleges report');
            }
        } catch (error) {
            console.error('Error fetching colleges report:', error);
            setError(error.response?.data?.message || 'Failed to fetch colleges report');
        } finally {
            setLoadingColleges(false);
        }
    };

    const fetchUsersReport = async () => {
        if (!selectedEvent) return;

        setLoadingUsers(true);
        setError('');
        setSelectedUser(null);
        setUserCollegesData([]);

        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${workshopDomain}/api/reports/users/${selectedEvent}`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.success) {
                setUsersData(response.data.data || []);
                setUsersTotalRegistrations(response.data.totalRegistrations || 0);
                setUsersUnassignedCount(response.data.unassignedCount || 0);
                setUsersTotalWithUnassigned(response.data.totalWithUnassigned || 0);
            } else {
                setError('Failed to fetch users report');
            }
        } catch (error) {
            console.error('Error fetching users report:', error);
            setError(error.response?.data?.message || 'Failed to fetch users report');
        } finally {
            setLoadingUsers(false);
        }
    };

    const fetchCollegesForUser = async (userId) => {
        if (!selectedEvent || !userId) return;

        setLoadingUserColleges(true);
        setError('');

        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(
                `${workshopDomain}/api/reports/users/${selectedEvent}/colleges/${userId}`,
                {
                    headers: { Authorization: `Bearer ${token}` }
                }
            );

            if (response.data.success) {
                setUserCollegesData(response.data.data || []);
                setUserCollegesTotalCount(response.data.totalCount || 0);
                setSelectedUser(userId);
            } else {
                setError('Failed to fetch user colleges report');
            }
        } catch (error) {
            console.error('Error fetching user colleges report:', error);
            setError(error.response?.data?.message || 'Failed to fetch user colleges report');
        } finally {
            setLoadingUserColleges(false);
        }
    };

    const handleEventChange = (event) => {
        setSelectedEvent(event.target.value);
        setStatesData([]);
        setStatesTotalCount(0);
        setCollegesData([]);
        setCollegesTotalCount(0);
        setUsersData([]);
        setUsersTotalRegistrations(0);
        setUsersUnassignedCount(0);
        setUsersTotalWithUnassigned(0);
        setUserCollegesData([]);
        setUserCollegesTotalCount(0);
        setSelectedState(null);
        setSelectedUser(null);
    };

    const handleStateClick = (stateName) => {
        fetchCollegesForState(stateName);
    };

    const handleUserClick = (userId) => {
        fetchCollegesForUser(userId);
    };

    const handleBackToStates = () => {
        setSelectedState(null);
        setCollegesData([]);
        setCollegesTotalCount(0);
        setMissingCollegeCodeCount(0);
    };

    const handleBackToUsers = () => {
        setSelectedUser(null);
        setUserCollegesData([]);
        setUserCollegesTotalCount(0);
    };

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
        setSelectedState(null);
        setSelectedUser(null);
        setCollegesData([]);
        setCollegesTotalCount(0);
        setUserCollegesData([]);
        setUserCollegesTotalCount(0);
    };

    useEffect(() => {
        if (selectedEvent) {
            if (activeTab === 0) {
                fetchStatesReport();
            } else {
                fetchUsersReport();
            }
        }
    }, [selectedEvent, activeTab]);

    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            <Paper sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <AssessmentIcon sx={{ mr: 2, fontSize: 32 }} />
                    <Typography variant="h4" component="h1">
                        Reports
                    </Typography>
                </Box>

                {/* Event Selector */}
                <Box sx={{ mb: 3 }}>
                    <FormControl fullWidth>
                        <InputLabel>Select Event</InputLabel>
                        <Select
                            value={selectedEvent}
                            onChange={handleEventChange}
                            label="Select Event"
                        >
                            {events.map((event) => (
                                <MenuItem key={event.hash_id} value={event.hash_id}>
                                    {event.title} ({event.event_type})
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                        {error}
                    </Alert>
                )}

                {/* Tabs */}
                <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                    <Tabs value={activeTab} onChange={handleTabChange}>
                        <Tab label="Report by States" />
                        <Tab label="Report by Users" />
                    </Tabs>
                </Box>

                {/* States Tab Content */}
                {activeTab === 0 && (
                    <Box>
                        {selectedState ? (
                            // Colleges drill-down view
                            <Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                    <Button
                                        startIcon={<ArrowBackIcon />}
                                        onClick={handleBackToStates}
                                        sx={{ mr: 2 }}
                                    >
                                        Back to States
                                    </Button>
                                    <Typography variant="h6">
                                        Colleges in {selectedState} ({collegesTotalCount})
                                        {missingCollegeCodeCount > 0 && (
                                            <Typography variant="body2" color="text.secondary" component="span" sx={{ ml: 1 }}>
                                                | Missing College Code: {missingCollegeCodeCount}
                                            </Typography>
                                        )}
                                    </Typography>
                                </Box>

                                {loadingColleges ? (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                                        <CircularProgress />
                                    </Box>
                                ) : (
                                    <TableContainer component={Paper}>
                                        <Table>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell><strong>College Name</strong></TableCell>
                                                    <TableCell><strong>College Code</strong></TableCell>
                                                    <TableCell align="right"><strong>Registrations</strong></TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {collegesData.length === 0 ? (
                                                    <TableRow>
                                                        <TableCell colSpan={3} align="center">
                                                            No colleges found
                                                        </TableCell>
                                                    </TableRow>
                                                ) : (
                                                    collegesData.map((college, index) => (
                                                        <TableRow key={index} hover>
                                                            <TableCell>{college.college_name}</TableCell>
                                                            <TableCell>{college.college_code || 'N/A'}</TableCell>
                                                            <TableCell align="right">
                                                                <Chip
                                                                    label={parseInt(college.count)}
                                                                    color="primary"
                                                                    size="small"
                                                                />
                                                            </TableCell>
                                                        </TableRow>
                                                    ))
                                                )}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                )}
                            </Box>
                        ) : (
                            // States view
                            <Box>
                                {statesTotalCount > 0 && (
                                    <Box sx={{ mb: 2 }}>
                                        <Typography variant="h6" color="primary">
                                            Total Registrations: {statesTotalCount}
                                        </Typography>
                                    </Box>
                                )}
                                {loadingStates ? (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                                        <CircularProgress />
                                    </Box>
                                ) : (
                                    <TableContainer component={Paper}>
                                        <Table>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell><strong>State</strong></TableCell>
                                                    <TableCell align="right"><strong>Registrations</strong></TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {statesData.length === 0 ? (
                                                    <TableRow>
                                                        <TableCell colSpan={2} align="center">
                                                            {selectedEvent
                                                                ? 'No registrations found for this event'
                                                                : 'Please select an event'}
                                                        </TableCell>
                                                    </TableRow>
                                                ) : (
                                                    statesData.map((state, index) => (
                                                        <TableRow
                                                            key={index}
                                                            hover
                                                            onClick={() => handleStateClick(state.state)}
                                                            sx={{ cursor: 'pointer' }}
                                                        >
                                                            <TableCell>{state.state}</TableCell>
                                                            <TableCell align="right">
                                                                <Chip
                                                                    label={parseInt(state.count)}
                                                                    color="primary"
                                                                    size="small"
                                                                />
                                                            </TableCell>
                                                        </TableRow>
                                                    ))
                                                )}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                )}
                            </Box>
                        )}
                    </Box>
                )}

                {/* Users Tab Content */}
                {activeTab === 1 && (
                    <Box>
                        {selectedUser ? (
                            // User colleges drill-down view
                            <Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                    <Button
                                        startIcon={<ArrowBackIcon />}
                                        onClick={handleBackToUsers}
                                        sx={{ mr: 2 }}
                                    >
                                        Back to Users
                                    </Button>
                                    <Typography variant="h6">
                                        Colleges visited by{' '}
                                        {usersData.find(u => u.user_id === selectedUser)?.userName || 'User'} ({userCollegesTotalCount})
                                    </Typography>
                                </Box>

                                {loadingUserColleges ? (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                                        <CircularProgress />
                                    </Box>
                                ) : (
                                    <TableContainer component={Paper}>
                                        <Table>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell><strong>College Name</strong></TableCell>
                                                    <TableCell><strong>College Code</strong></TableCell>
                                                    <TableCell align="right"><strong>Registrations</strong></TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {userCollegesData.length === 0 ? (
                                                    <TableRow>
                                                        <TableCell colSpan={3} align="center">
                                                            No colleges with registrations found
                                                        </TableCell>
                                                    </TableRow>
                                                ) : (
                                                    userCollegesData.map((college, index) => (
                                                        <TableRow key={index} hover>
                                                            <TableCell>{college.college_name}</TableCell>
                                                            <TableCell>{college.college_code}</TableCell>
                                                            <TableCell align="right">
                                                                <Chip
                                                                    label={college.count}
                                                                    color="primary"
                                                                    size="small"
                                                                />
                                                            </TableCell>
                                                        </TableRow>
                                                    ))
                                                )}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                )}
                            </Box>
                        ) : (
                            // Users view
                            <Box>
                                {usersTotalWithUnassigned > 0 && (
                                    <Box sx={{ mb: 2 }}>
                                        <Typography variant="h6" color="primary">
                                            Total Registrations: {usersTotalWithUnassigned}
                                            {usersUnassignedCount > 0 && (
                                                <Typography variant="body2" color="text.secondary" component="span" sx={{ ml: 1 }}>
                                                    (Assigned: {usersTotalRegistrations}, Unassigned: {usersUnassignedCount})
                                                </Typography>
                                            )}
                                        </Typography>
                                    </Box>
                                )}
                                {loadingUsers ? (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                                        <CircularProgress />
                                    </Box>
                                ) : (
                                    <TableContainer component={Paper}>
                                        <Table>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell><strong>Resource Name</strong></TableCell>
                                                    <TableCell><strong>Email</strong></TableCell>
                                                    <TableCell align="right"><strong>Total Registrations</strong></TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {usersData.length === 0 ? (
                                                    <TableRow>
                                                        <TableCell colSpan={3} align="center">
                                                            {selectedEvent
                                                                ? 'No users found or no registrations for visited colleges'
                                                                : 'Please select an event'}
                                                        </TableCell>
                                                    </TableRow>
                                                ) : (
                                                    usersData.map((user, index) => (
                                                        <TableRow
                                                            key={index}
                                                            hover
                                                            onClick={() => handleUserClick(user.user_id)}
                                                            sx={{ cursor: 'pointer' }}
                                                        >
                                                            <TableCell>{user.userName}</TableCell>
                                                            <TableCell>{user.email}</TableCell>
                                                            <TableCell align="right">
                                                                <Chip
                                                                    label={user.total_registrations}
                                                                    color="primary"
                                                                    size="small"
                                                                />
                                                            </TableCell>
                                                        </TableRow>
                                                    ))
                                                )}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                )}
                            </Box>
                        )}
                    </Box>
                )}
            </Paper>
        </Container>
    );
};

export default Reports;

